if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Guard against a stale "sold out" state lingering on the button the
        // next time the customer hovers/interacts with this card after a
        // previous add-to-cart attempt (success OR failure). Without this,
        // the "Add to cart" label and the "Sold out" label can end up
        // visible at the same time because the sold-out branch only ever
        // *shows* the sold-out message and never gets reset back.
        this.addEventListener('mouseenter', this.resetSoldOutState.bind(this));
        this.addEventListener('pointerenter', this.resetSoldOutState.bind(this));
      }

      resetSoldOutState() {
        if (!this.error) return;

        const label = this.submitButton.querySelector('span');
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');

        if (label) {
          label.classList.remove('hidden');
          label.style.removeProperty('display');
        }
        if (soldOutMessage) {
          soldOutMessage.classList.add('hidden');
          soldOutMessage.style.display = 'none';
        }

        this.error = false;
        if (!this.submitButton.hasAttribute('disabled')) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      onSubmitHandler(evt) {
          evt.preventDefault();
          if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

          this.handleErrorMessage();

          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner').classList.remove('hidden');

          // Force the "Sold out" label to stay hidden for the entire
          // duration of the request. A CSS rule in quick-add.css
          // (`.quick-add__submit[aria-disabled="true"] > .sold-out-message.hidden`)
          // tries to do this already, but it can lose the specificity race
          // against other card/button styles while the button is in its
          // loading state, which is what causes "Sold out" to flash next to
          // "Add to cart" right after clicking. An inline style always wins,
          // so this guarantees it can't happen regardless of stock status.
          const soldOutMessageEl =
            this.submitButton.querySelector('.sold-out-message');

          if (soldOutMessageEl) {
            soldOutMessageEl.classList.add('hidden');
            soldOutMessageEl.style.setProperty('display', 'none', 'important');
          }

          const formData = new FormData(this.form);

          const upsellBox = document.querySelector('upsell-add-on');
          if (upsellBox) {
            const selectedUpsells = upsellBox.getSelectedProducts();

            if (selectedUpsells && selectedUpsells.length > 0) {
              selectedUpsells.forEach((product, index) => {
                formData.append(`items[${index + 1}][id]`, product.variantId);
                formData.append(`items[${index + 1}][quantity]`, 1);
              });
            }
          }

          this.submitAddToCart(formData, soldOutMessageEl, true);
      }

      // Posts the add-to-cart request. On a genuine "sold out"/insufficient
      // stock response we used to lock the button into the Sold Out state
      // immediately and permanently (only cleared on the next mouseenter).
      // In practice, two Add to Cart clicks fired in quick succession on
      // different product cards can make Shopify's cart endpoint reject one
      // of the two requests as a transient conflict even though the item is
      // genuinely in stock (confirmed via a screen recording: adding the
      // Tinted Nail Hardener right after the Comfort Hardener made the
      // Tinted card's button get stuck on "Sold out" even though that same
      // variant adds to cart fine on its own). To guard against that, a
      // failed request is retried once automatically before we commit to
      // showing "Sold out" to the customer.
      submitAddToCart(formData, soldOutMessageEl, allowRetry) {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.set(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.set('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }

          config.body = formData;

          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                if (allowRetry) {
                  // Likely a transient conflict from a near-simultaneous
                  // add-to-cart request elsewhere on the page. Wait a beat
                  // for the other request to settle, then try this one
                  // again before treating it as a real error.
                  setTimeout(() => this.submitAddToCart(formData, soldOutMessageEl, false), 400);
                  return;
                }

                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                const label = this.submitButton.querySelector('span');
                if (label) {
                  label.classList.add('hidden');
                  // Belt-and-braces: force the hide even if the "hidden"
                  // utility class isn't present/active for this element for
                  // any reason (e.g. a stylesheet load race), so the two
                  // labels can never render on top of each other.
                  label.style.display = 'none';
                }
                soldOutMessage.classList.remove('hidden');
                soldOutMessage.style.removeProperty('display');
                this.error = true;

                // NOTE: we deliberately do NOT auto-clear this after a delay.
                // By the time we get here, the automatic retry above has
                // already ruled out a transient/race failure, so this is
                // treated as a genuine "no stock available" response from
                // Shopify. It should keep showing "Sold out" until the
                // customer interacts with the button again (handled by
                // resetSoldOutState() on mouseenter/pointerenter), not clear
                // itself on a timer — otherwise a truly sold-out item would
                // flip back to "Add to cart" on its own after a few seconds.
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }

              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              const quickViewModal = document.getElementById('product-modal');

              if (quickViewModal) {
                quickViewModal.classList.remove('active');
                document.body.classList.remove('quickview-open');
              }

              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
              }
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');

              if (this.cart && this.cart.classList.contains('is-empty')) {
                this.cart.classList.remove('is-empty');
              }

              if (!this.error) {
                this.submitButton.removeAttribute('aria-disabled');

                // 정상 처리되면 Sold out은 계속 숨겨둠
                if (soldOutMessageEl) {
                  soldOutMessageEl.classList.add('hidden');
                  soldOutMessageEl.style.setProperty('display', 'none', 'important');
                }
              }

              this.querySelector('.loading__spinner').classList.add('hidden');
            });
      }


      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}